import React, { createContext, useState, useContext, useEffect } from 'react';
import { firestore } from 'config/firebase';
import { doc, setDoc, getDoc, updateDoc, deleteDoc } from 'firebase/firestore';
import { useAuthContext } from './AuthContext';

const CartContext = createContext();

export const useCartContext = () => useContext(CartContext);

export const CartProvider = ({ children }) => {
    const { user, isAuthenticated } = useAuthContext();
    const [cartItems, setCartItems] = useState([]);
    const MAX_CART_ITEMS = 10;

    // Get user cart from Firestore
    const fetchCart = async (userId) => {
        const cartDoc = await getDoc(doc(firestore, "carts", userId));
        if (cartDoc.exists()) {
            const items = cartDoc.data().items || [];
            setCartItems(items.slice(0, MAX_CART_ITEMS));
        } else {
            setCartItems([]);
        }
    };

    // Add item to cart
    const addToCart = async (product) => {
        const existingProductIndex = cartItems.findIndex(item => item.id === product.id);
        let updatedCart = [];

        if (existingProductIndex !== -1) {
            // Product exists, update quantity
            updatedCart = cartItems.map((item, index) =>
                index === existingProductIndex ? { ...item, quantity: Math.min(item.quantity + 1, 99) } : item
            );
        } else {
            // New product
            if (cartItems.length >= MAX_CART_ITEMS) {
                console.warn("Cart is full. Cannot add more items.");
                return;
            }
            updatedCart = [...cartItems, { ...product, quantity: 1 }].slice(0, MAX_CART_ITEMS);
        }

        setCartItems(updatedCart);
        if (user) {
            await setDoc(doc(firestore, "carts", user.uid), { items: updatedCart });
        }
    };

    // Remove item from cart
    const removeFromCart = async (productId) => {
        const updatedCart = cartItems.filter(item => item.id !== productId);
        setCartItems(updatedCart);
        if (user) {
            await updateDoc(doc(firestore, "carts", user.uid), { items: updatedCart });
        }
    };

    // Update cart item quantity
    const updateCartItem = async (productId, quantity) => {
        if (quantity < 1 || quantity > 99) {
            console.warn("Invalid quantity. Must be between 1 and 99.");
            return;
        }
        const updatedCart = cartItems.map(item =>
            item.id === productId ? { ...item, quantity } : item
        );
        setCartItems(updatedCart);
        if (user) {
            await updateDoc(doc(firestore, "carts", user.uid), { items: updatedCart });
        }
    };

    // Clear the cart
    const clearCart = async () => {
        setCartItems([]);
        if (user) {
            await deleteDoc(doc(firestore, "carts", user.uid));
        }
    };

    useEffect(() => {
        if (isAuthenticated && user && user.uid) {
            fetchCart(user.uid);
        } else {
            setCartItems([]);
        }
    }, [isAuthenticated, user]);

    return (
        <CartContext.Provider value={{ cartItems, addToCart, removeFromCart, updateCartItem, clearCart }}>
            {children}
        </CartContext.Provider>
    );
};